import React, { memo, useMemo } from 'react';

// ── Primitives ────────────────────────────────────────────────
const Pill = memo(({ value, color }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${color}`}>{value}</span>
));

const MiniBar = memo(({ pct, color }) => (
  <div className="h-1 bg-white/20 rounded-full overflow-hidden mt-2">
    <div className={`h-full ${color} rounded-full`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
));

// ── Hero KPI Cards ────────────────────────────────────────────
const HeroCards = memo(({ stats, derived }) => {
  const { total, successRate, upiPct, codPct, avgRevenue, revenuePerUser } = derived;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Revenue */}
      <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl p-4 text-white shadow-md relative overflow-hidden">
        <div className="absolute -right-5 -top-5 w-20 h-20 bg-white/10 rounded-full" />
        <div className="absolute right-2 bottom-2 w-10 h-10 bg-white/5 rounded-full" />
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium opacity-60 uppercase tracking-wider">Revenue</span>
          <div className="bg-white/20 rounded-lg p-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
        </div>
        <p className="text-2xl font-bold tracking-tight">₹{stats.totalRevenue.toLocaleString()}</p>
        <div className="mt-2 pt-2 border-t border-white/15 grid grid-cols-2 gap-1 text-xs">
          <div><p className="opacity-50">Avg/order</p><p className="font-semibold">₹{avgRevenue.toLocaleString()}</p></div>
          <div><p className="opacity-50">Per user</p><p className="font-semibold">₹{revenuePerUser.toLocaleString()}</p></div>
        </div>
      </div>

      {/* Orders */}
      <div className="bg-gradient-to-br from-orange-500 to-rose-600 rounded-2xl p-4 text-white shadow-md relative overflow-hidden">
        <div className="absolute -right-5 -top-5 w-20 h-20 bg-white/10 rounded-full" />
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium opacity-60 uppercase tracking-wider">Orders</span>
          <div className="bg-white/20 rounded-lg p-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
        </div>
        <p className="text-2xl font-bold tracking-tight">{total.toLocaleString()}</p>
        <MiniBar pct={parseFloat(successRate)} color="bg-white/70" />
        <div className="mt-2 grid grid-cols-3 gap-1 text-xs text-center">
          <div><p className="font-bold">{stats.delivered}</p><p className="opacity-50">Done</p></div>
          <div><p className="font-bold">{stats.running}</p><p className="opacity-50">Live</p></div>
          <div><p className="font-bold">{stats.cancelled}</p><p className="opacity-50">Void</p></div>
        </div>
      </div>

      {/* Users */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 text-white shadow-md relative overflow-hidden">
        <div className="absolute -right-5 -top-5 w-20 h-20 bg-white/10 rounded-full" />
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium opacity-60 uppercase tracking-wider">Users</span>
          <div className="bg-white/20 rounded-lg p-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </div>
        <p className="text-2xl font-bold tracking-tight">{stats.totalUsers.toLocaleString()}</p>
        <div className="mt-2 pt-2 border-t border-white/15 grid grid-cols-2 gap-1 text-xs">
          <div><p className="opacity-50">Orders/user</p><p className="font-semibold">{(total / (stats.totalUsers || 1)).toFixed(1)}</p></div>
          <div><p className="opacity-50">Rev/user</p><p className="font-semibold">₹{revenuePerUser.toLocaleString()}</p></div>
        </div>
      </div>

      {/* Payments */}
      <div className="bg-gradient-to-br from-sky-500 to-blue-700 rounded-2xl p-4 text-white shadow-md relative overflow-hidden">
        <div className="absolute -right-5 -top-5 w-20 h-20 bg-white/10 rounded-full" />
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium opacity-60 uppercase tracking-wider">Payments</span>
          <div className="bg-white/20 rounded-lg p-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
        </div>
        <p className="text-2xl font-bold tracking-tight">{(stats.upi + stats.cod).toLocaleString()}</p>
        <div className="flex h-1.5 rounded-full overflow-hidden bg-white/20 mt-2">
          <div className="bg-white/80 h-full transition-all" style={{ width: `${upiPct}%` }} />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
          <div><p className="opacity-50">UPI {upiPct}%</p><p className="font-semibold">{stats.upi}</p></div>
          <div><p className="opacity-50">COD {codPct}%</p><p className="font-semibold">{stats.cod}</p></div>
        </div>
      </div>
    </div>
  );
});

// ── Stat Pills ────────────────────────────────────────────────
const StatPills = memo(({ stats, derived }) => {
  const { successRate, runningRate, cancelRate, upiPct, codPct } = derived;

  const pills = useMemo(() => [
    { label: 'Delivered', value: stats.delivered, badge: `${successRate}%`, badgeColor: 'bg-emerald-100 text-emerald-700', icon: '✓', iconBg: 'bg-emerald-50 text-emerald-600' },
    { label: 'Running', value: stats.running, badge: `${runningRate}%`, badgeColor: 'bg-amber-100 text-amber-700', icon: '⟳', iconBg: 'bg-amber-50 text-amber-600' },
    { label: 'Cancelled', value: stats.cancelled, badge: `${cancelRate}%`, badgeColor: parseFloat(cancelRate) > 15 ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-600', icon: '✕', iconBg: 'bg-rose-50 text-rose-500' },
    { label: 'UPI', value: stats.upi, badge: `${upiPct}%`, badgeColor: 'bg-blue-100 text-blue-700', icon: '⚡', iconBg: 'bg-blue-50 text-blue-600' },
    { label: 'COD', value: stats.cod, badge: `${codPct}%`, badgeColor: 'bg-purple-100 text-purple-700', icon: '₹', iconBg: 'bg-purple-50 text-purple-600' },
  ], [stats, successRate, runningRate, cancelRate, upiPct, codPct]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {pills.map((s, i) => (
        <div key={i} className="bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${s.iconBg}`}>{s.icon}</div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-400">{s.label}</p>
            <p className="text-lg font-bold text-gray-800 leading-tight">{s.value.toLocaleString()}</p>
          </div>
          <Pill value={s.badge} color={s.badgeColor} />
        </div>
      ))}
    </div>
  );
});

// ── Order Distribution Bar ────────────────────────────────────
const OrderDistributionBar = memo(({ derived }) => {
  const { successRate, runningRate, cancelRate } = derived;
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-5 py-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order Distribution</span>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />Delivered {successRate}%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />Running {runningRate}%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400 inline-block" />Cancelled {cancelRate}%</span>
        </div>
      </div>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
        <div className="bg-emerald-500 h-full rounded-l-full transition-all" style={{ width: `${successRate}%` }} />
        <div className="bg-amber-400 h-full transition-all" style={{ width: `${runningRate}%` }} />
        <div className="bg-rose-400 h-full rounded-r-full flex-1 transition-all" />
      </div>
    </div>
  );
});

// ── Main MetricsCards ─────────────────────────────────────────
const MetricsCards = memo(({ stats }) => {
  const derived = useMemo(() => {
    const total = stats.delivered + stats.running + stats.cancelled || 1;
    const upiTotal = stats.upi + stats.cod || 1;
    const upiPct = Math.round((stats.upi / upiTotal) * 100);
    return {
      total,
      successRate: ((stats.delivered / total) * 100).toFixed(1),
      cancelRate: ((stats.cancelled / total) * 100).toFixed(1),
      runningRate: ((stats.running / total) * 100).toFixed(0),
      upiPct,
      codPct: 100 - upiPct,
      avgRevenue: Math.round(stats.totalRevenue / total || 0),
      revenuePerUser: Math.round(stats.totalRevenue / (stats.totalUsers || 1)),
    };
  }, [stats]);

  return (
    <div className="col-span-12 space-y-3">
      <HeroCards stats={stats} derived={derived} />
      <StatPills stats={stats} derived={derived} />
      <OrderDistributionBar derived={derived} />
    </div>
  );
});

export default MetricsCards;
